import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import Title from "common/Typography/Title";
import React, { CSSProperties, memo, useEffect, useState } from "react";
import styled from "styled-components";
import { MOBILE_VIRTUAL_LIST, TIME_RANGE_START } from "common/constants";
import {
  getFirstLastInGraphData,
  getPriceChangePercentage,
} from "features/dashboard/common/utils";
import Flex from "common/Boxes/Flex";
import PriceChange from "common/Charts/PriceChange";
import CardChart from "features/dashboard/CardChart";
import useMedia from "utils/hooks/useMedia";
import { hexToRGB } from "@shared/utils/misc";
import TextWithIcon from "common/Text/TextWithIcon";
import { DOLLAR_COINS, ICON_TYPES } from "@shared/common/constants";
import useLiveGraphData from "@shared/services/socket/useLiveGraphData";
import { toFixed } from "@shared/utils/numbers";
import { LineChartDataPointWithExchange, SocketTradeData } from "@shared/types";
import Text from "common/Typography/Text";
import { useTradePriceChart } from "@shared/features/trades/common/hooks";
import { sharedLib } from "@shared/services/shared.service";
import useTradeCard from "features/dashboard/common/useTradeCard";
import { StyledCard } from "features/dashboard/common/styles";
import Tooltip from "common/Tooltips/Tooltip";

interface TradeCardProps {
  tradeData: SocketTradeData;
  /** Stretches the card to its grid cell instead of the virtual list size. */
  fluid?: boolean;
}

const TradeCard = ({ tradeData, fluid }: TradeCardProps) => {
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_VIRTUAL_LIST})`],
    [true],
    false
  );
  const [changePercentage, setChangePercentage] = useState<
    number | undefined
  >();
  const [changeValue, setChangeValue] = useState<number>();

  const {
    getPriceGraphDataStatus,
    formattedGraphData,
    args: graphDataArgs,
    precision: graphPrecision,
  } = useTradePriceChart(CHART_TIME_RANGE, tradeData);
  const {
    data: priceGraphData,
    isFetching: priceGraphDataFetching,
    isSuccess: priceGraphDataSuccess,
  } = getPriceGraphDataStatus;

  const priceCoin = priceGraphData?.data.price_coin || "";
  const isDollarSymbol = DOLLAR_COINS.includes(priceCoin);
  const graphData = priceGraphData?.data.graph_data[CHART_TIME_RANGE];

  useLiveGraphData<LineChartDataPointWithExchange>(
    sharedLib.sharedApi.common,
    "getPriceGraphData",
    graphDataArgs,
    CHART_TIME_RANGE,
    tradeData.symbol,
    tradeData.exchange,
    true
  );

  const {
    theme,
    openTradeExtraDetails,
    topSection,
    bottomSection,
    invalidAccountData,
    isTradeClosed,
    annotations,
  } = useTradeCard({
    tradeData,
    graphData,
  });

  useEffect(() => {
    if (graphData) {
      const [first, last] = getFirstLastInGraphData(graphData, 2);
      const change = toFixed(last - first, graphPrecision);
      if (!priceGraphDataFetching) {
        setChangePercentage(getPriceChangePercentage(first, last));
        setChangeValue(change);
      }
    }
  }, [graphData]);

  const changeValueWithIcon = isDollarSymbol ? (
    <Title
      level={5}
      type="secondary"
      style={{
        marginRight: 4,
        lineHeight: "18px",
        fontSize: theme.typography.fontSizes.M,
      }}
    >
      ${changeValue}
    </Title>
  ) : (
    <TextWithIcon
      text={changeValue}
      coinOrExchangeName={priceGraphData?.data.price_coin}
      iconType={ICON_TYPES.COIN}
      iconSize={"m"}
      style={{
        color: theme.colors.secondaryGrey,
        marginRight: 4,
      }}
    />
  );

  const change = (
    <PriceChangeWrapper>
      {changeValueWithIcon}
      {changePercentage !== undefined && (
        <PriceChange change={changePercentage} size={11} useBackground={true} />
      )}
    </PriceChangeWrapper>
  );

  const chart = (
    <CardChart
      graphData={priceGraphDataFetching ? [] : formattedGraphData}
      loading={priceGraphDataFetching}
      selectedTimeRange={CHART_TIME_RANGE}
      annotations={formattedGraphData.length ? annotations : []}
      useExtendToAnnotations={true}
      hideTimeRanges={true}
      precision={graphPrecision}
      numOfXTicks={4}
      height={150}
      isSuccess={priceGraphDataSuccess}
    />
  );

  const textStyle: CSSProperties = {
    color: invalidAccountData.color ?? theme.colors.textColor,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const accountAndGroup = (
    <Flex style={{ marginTop: 12 }}>
      <Tooltip title={tradeData.account} placement="topLeft">
        <Flex style={{ maxWidth: "60%", overflow: "hidden" }}>
          <TextWithIcon
            text={tradeData.account}
            coinOrExchangeName={tradeData.exchange}
            iconType={ICON_TYPES.EXCHANGE}
            textStyle={textStyle}
            style={{ maxWidth: "100%" }}
          />
        </Flex>
      </Tooltip>
      <div style={{ maxWidth: "40%", overflow: "hidden" }}>
        <Tooltip title={tradeData.group}>
          <GroupTag $ellipsis={true}>{tradeData.group}</GroupTag>
        </Tooltip>
      </div>
    </Flex>
  );

  return (
    <StyledCard
      $isMobile={isMobile}
      $isClosed={!!isTradeClosed}
      $fluid={fluid}
      style={openTradeExtraDetails ? undefined : { cursor: "default" }}
    >
      <div onClick={openTradeExtraDetails}>
        {topSection}
        {change}
      </div>
      {chart}
      <div onClick={openTradeExtraDetails}>
        {bottomSection}
        <HorizontalBorder />
        {accountAndGroup}
      </div>
    </StyledCard>
  );
};

const MemoizedTradeCard = memo(TradeCard);
export default MemoizedTradeCard;

const CHART_TIME_RANGE = TIME_RANGE_START;

const HorizontalBorder = styled.div`
  border-bottom: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
  width: 100%;
  padding-top: 8px;
`;
const GroupTag = styled(Text)`
  background: ${(p) => hexToRGB(p.theme.colors.secondaryGrey, 0.1)};
  border-radius: 4px;
  padding: 2px 8px;
  max-width: 100%;
  ${(p) =>
    p.theme.isNewLook &&
    `
    border-radius: ${NEW_LOOK_RADIUS.chip}px;
    padding: 2px 10px;
  `}
`;
const PriceChangeWrapper = styled.div`
  display: flex;
  float: right;
`;
